'use client';

import { useBunja } from 'bunja/react';
import html2canvas from 'html2canvas';
import { useAtomValue } from 'jotai';
import { useTranslations } from 'next-intl';
import { QRCodeCanvas } from 'qrcode.react';
import { useContext, useEffect, useMemo, useRef, useState } from 'react';
import { useCopyToClipboard } from 'react-use';

import { FormattedMessage } from '@flipster/i18n';
import { useOverlayItem } from '@flipster/overlay';
import { IconButton } from '@flipster/react/design-system/icon-button';
import { Modal } from '@flipster/react/design-system/modal';
import { snackbar } from '@flipster/react/design-system/snackbar';
import { Spinner } from '@flipster/react/design-system/spinner';
import { TextButton } from '@flipster/react/design-system/text-button';
import DownloadIcon from '@flipster/react/icon/generated/DownloadIcon';
import FacebookIcon from '@flipster/react/icon/generated/FacebookIcon';
import LinkIcon from '@flipster/react/icon/generated/LinkIcon';
import TelegramIcon from '@flipster/react/icon/generated/TelegramIcon';
import TwitterIcon from '@flipster/react/icon/generated/TwitterIcon';
import { css } from '@flipster/styled-system/css';
import { center, hstack } from '@flipster/styled-system/patterns';
import { convertWebviewToWebUrl } from '@flipster/utils/env-helper';
import { downloadImageFile } from '@flipster/utils/file';
import segment from '@flipster/utils/segment';
import { CompetitionsEvent } from '@flipster/utils/segment/events/competitions';

import { authBunja } from '../../../shared/auth/state';
import { useReferralCode } from '../../../shared/referral-code/state';
import { useIsWebview } from '../../../shared/webview';
import { CompetitionIdContext, competitionBunja } from '../state';
import shareDefaultImage from './share-default-image.webp';

export const EventShareModal = () => {
  const id = useContext(CompetitionIdContext);
  const { isOpen, close, unmount } = useOverlayItem();
  const [, copyToClipboard] = useCopyToClipboard();
  const t = useTranslations();
  const isWebview = useIsWebview();

  const { competitionContentful, competitionAtom } = useBunja(competitionBunja);
  const competition = useAtomValue(competitionAtom);
  const { isCookieLoggedInAtom } = useBunja(authBunja);
  const isCookieLoggedIn = useAtomValue(isCookieLoggedInAtom);
  const { referralCode, isLoading: isReferralCodeLoading } = useReferralCode();
  const [isImageLoaded, setIsImageLoaded] = useState(false);
  const cardRef = useRef<HTMLDivElement>(null);

  const shareCardImageUrl = competitionContentful?.shareCardImage?.url ?? (shareDefaultImage as { src: string }).src;
  const defaultShareText = competitionContentful?.subtitle
    ? t('Campaign.r260319.Share_panel_text', {
        subtitle: competitionContentful.subtitle,
        prizePool: competition?.maxPrizePool
          ? `$${Math.max(
              Number(competition.maxPrizePool.pnl || 0),
              Number(competition.maxPrizePool.volume || 0),
              Number(competition.maxPrizePool.roi || 0),
            ).toLocaleString()}`
          : '',
      })
    : undefined;
  const shareText = competitionContentful?.shareCardText ?? defaultShareText;

  useEffect(() => {
    if (!shareCardImageUrl) {
      setIsImageLoaded(true);
      return;
    }
    const img = new Image();
    img.onload = () => setIsImageLoaded(true);
    img.onerror = () => setIsImageLoaded(true);
    img.src = shareCardImageUrl;
  }, [shareCardImageUrl]);

  const isLoading = (isCookieLoggedIn && isReferralCodeLoading) || !isImageLoaded;
  const shareUrlString = useMemo(() => {
    const url = new URL(convertWebviewToWebUrl(window.location.pathname));
    if (referralCode) {
      url.searchParams.set('referral_code', referralCode);
      url.searchParams.set('referral_type', 'copy');
    }
    return url.toString();
  }, [referralCode]);

  useEffect(() => {
    if (isOpen) {
      segment.track(CompetitionsEvent.CompetitionTradingSharePanelView, {
        competition_id: id,
        is_logged_in: isCookieLoggedIn,
        referral_code: referralCode,
      });
    }
  }, [isOpen, id, isCookieLoggedIn, referralCode]);

  const trackPanelClick = (shareType: 'telegram' | 'x' | 'facebook' | 'copy_link' | 'download') => {
    segment.track(CompetitionsEvent.CompetitionTradingSharePanelClick, {
      competition_id: id,
      is_logged_in: isCookieLoggedIn,
      referral_code: referralCode,
      share_type: shareType,
    });
  };

  const handleTelegramShare = () => {
    trackPanelClick('telegram');
    const telegramUrl = shareText
      ? `https://t.me/share/url?url=${encodeURIComponent(shareUrlString)}&text=${encodeURIComponent(shareText)}`
      : `https://t.me/share/url?url=${encodeURIComponent(shareUrlString)}`;
    window.open(telegramUrl, '_blank');
  };

  const handleTwitterShare = () => {
    trackPanelClick('x');
    const twitterUrl = shareText
      ? `https://x.com/intent/tweet?url=${encodeURIComponent(shareUrlString)}&text=${encodeURIComponent(shareText)}`
      : `https://x.com/intent/tweet?url=${encodeURIComponent(shareUrlString)}`;
    window.open(twitterUrl, '_blank');
  };

  const handleFacebookShare = () => {
    trackPanelClick('facebook');
    window.open(`https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(shareUrlString)}`, '_blank');
  };

  const handleDownload = async () => {
    if (!cardRef.current) return;
    trackPanelClick('download');
    try {
      const canvas = await html2canvas(cardRef.current, {
        scale: 3,
        useCORS: true,
      });
      const dataUrl = canvas.toDataURL('image/png');
      downloadImageFile('event-share', dataUrl);
    } catch {
      if (shareCardImageUrl) window.open(shareCardImageUrl, '_blank');
    }
  };

  const handleCopyLink = () => {
    trackPanelClick('copy_link');
    const text = referralCode ? `${shareUrlString}\n${referralCode}` : shareUrlString;
    copyToClipboard(text);
    snackbar.success({
      message: referralCode ? (
        <FormattedMessage id='Competition.r251215.Link_and_code_copied_successfully' />
      ) : (
        <FormattedMessage id='Competition.r251215.Link_copied_successfully' />
      ),
    });
  };

  return (
    <Modal.Root isOpen={isOpen} onClose={close} onExit={unmount} mode='fullModal' size='small'>
      <Modal.Content
        css={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          gap: 24,
          px: { base: 16, tablet: 40 },
          pb: 40,
        }}
      >
        {isLoading ? (
          <div className={center({ w: '100%', py: 40 })}>
            <Spinner size={32} color='gray' />
          </div>
        ) : (
          <>
            {shareCardImageUrl && (
              <div
                ref={cardRef}
                className={css({
                  position: 'relative',
                  w: 280,
                  rounded: 28,
                  overflow: 'hidden',
                  flexShrink: 0,
                })}
              >
                <img
                  src={shareCardImageUrl}
                  alt='Event share card'
                  className={css({
                    w: '100%',
                    objectFit: 'cover',
                  })}
                />
                {referralCode && (
                  <div
                    className={hstack({
                      position: 'absolute',
                      bottom: 0,
                      left: 0,
                      right: 0,
                      gap: 40,
                      p: 16,
                      bg: 'rgba(0, 0, 0, 0.4)',
                      backdropFilter: 'blur(2px)',
                    })}
                  >
                    <div className={css({ display: 'flex', flexDirection: 'column', gap: 4, flex: 1 })}>
                      <span className={css({ textStyle: 'Text/S/Regular', color: 'content.default.level-3' })}>
                        Referral code
                      </span>
                      <span className={css({ textStyle: 'Text/S/Strong', color: 'content.default.level-5' })}>
                        {referralCode}
                      </span>
                    </div>
                    <div className={center({ p: 4, rounded: 4, bgColor: 'inverse.white' })}>
                      <QRCodeCanvas value={shareUrlString} size={42} />
                    </div>
                  </div>
                )}
              </div>
            )}

            <div className={hstack({ gap: 16, justify: 'center', w: '100%' })}>
              <IconButton
                size='large'
                hierarchy='quaternary'
                icon={<TelegramIcon size={20} color='white' />}
                onClick={handleTelegramShare}
                aria-label='Telegram'
                css={{ bg: '#0095fb' }}
              />
              <IconButton
                size='large'
                hierarchy='quaternary'
                icon={<TwitterIcon size={20} color='black' />}
                onClick={handleTwitterShare}
                aria-label='X'
                css={{ bgColor: 'content.default.level-3' }}
              />
              <IconButton
                size='large'
                hierarchy='quaternary'
                icon={<FacebookIcon size={20} color='white' />}
                onClick={handleFacebookShare}
                aria-label='Facebook'
                css={{ bg: '#337fff' }}
              />
              {!isWebview && (
                <IconButton
                  size='large'
                  hierarchy='tertiary'
                  icon={<DownloadIcon size={20} />}
                  onClick={handleDownload}
                  aria-label='Download'
                />
              )}
              <IconButton
                size='large'
                hierarchy='tertiary'
                icon={<LinkIcon size={20} />}
                onClick={handleCopyLink}
                aria-label='Copy link'
              />
            </div>

            {!isCookieLoggedIn && (
              <div className={hstack({ gap: 8, justify: 'center', w: '100%' })}>
                <span className={css({ textStyle: 'Text/M/Regular', color: 'content.default.level-2' })}>
                  <FormattedMessage id='Campaign.r260320.Share_your_referral_link' />
                </span>
                <TextButton
                  as='a'
                  href='/login'
                  color='accent'
                  label={<FormattedMessage id='Autogen_common.r251126.s_Log_in' />}
                  onClick={close}
                />
              </div>
            )}
          </>
        )}
      </Modal.Content>
    </Modal.Root>
  );
};
